import { type ButtonHTMLAttributes, forwardRef } from "react";
import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "@/lib/utils";

const buttonVariants = tv({
  base: [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "font-medium select-none",
    "rounded-lg",
    "cursor-pointer",
    "transition-[background-color,box-shadow,transform,color] duration-150 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "active:translate-y-px",
  ],
  variants: {
    variant: {
      primary: [
        "bg-primary text-primary-foreground",
        "shadow-[inset_0_-1px_0_rgba(0,0,0,0.2),0_1px_0_rgba(0,0,0,0.05)]",
        "hover:bg-primary-hovered",
        "active:bg-primary-pressed",
      ],
      secondary: [
        "bg-card text-text",
        "border border-border-strong",
        "shadow-[0_1px_0_rgba(0,0,0,0.05)]",
        "hover:bg-surface-hovered",
        "active:bg-surface-pressed",
      ],
      tertiary: [
        "bg-transparent text-text",
        "hover:bg-surface-hovered",
        "active:bg-surface-pressed",
      ],
      plain: [
        "bg-transparent text-primary min-w-0 px-1",
        "hover:underline underline-offset-2",
      ],
      critical: [
        "bg-destructive text-white",
        "shadow-[inset_0_-1px_0_rgba(0,0,0,0.2)]",
        "hover:bg-destructive-hovered",
      ],
      criticalSecondary: [
        "bg-card text-destructive",
        "border border-destructive",
        "hover:bg-destructive-background",
      ],
    },
    size: {
      sm: "h-7 px-3 text-xs",
      md: "h-9 min-w-20 px-3 text-[13px]",
      lg: "h-11 min-w-24 px-4 text-sm",
    },
    fullWidth: {
      true: "w-full",
    },
  },
  defaultVariants: {
    variant: "secondary",
    size: "md",
  },
});

type Props = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    loading?: boolean;
  };

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      loading,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        disabled={disabled ?? loading}
        {...props}
      >
        {loading && (
          <span
            aria-hidden
            className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
          />
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = "Button";
